/**
 * Pure helpers for the conversation inbox (tracker item 56): labels, paths,
 * the error messages staff see, delivery status text. No React, no API.
 */
import * as types from '../../api/types';
import { getErrorCode, getErrorMessage } from '../../utils/apiError';

export const INBOX_PATH = '/inbox';
export const INBOX_UNMATCHED_PATH = '/inbox/unmatched';

export const inboxThreadPath = (id: string) => `${INBOX_PATH}/${encodeURIComponent(id)}`;

/** The inbox filtered to a customer or a job (the Conversations sections link here). */
export function inboxFilterPath(filter: { customerId?: string; jobId?: string }): string {
  const params = new URLSearchParams();
  if (filter.customerId) params.set('customerId', filter.customerId);
  if (filter.jobId) params.set('jobId', filter.jobId);
  const query = params.toString();
  return query ? `${INBOX_PATH}?${query}` : INBOX_PATH;
}

export const CONVERSATION_STATUS_LABELS: Record<types.ConversationStatus, string> = {
  OPEN: 'Open',
  WAITING_ON_CUSTOMER: 'Waiting on customer',
  CLOSED: 'Closed',
  UNMATCHED: 'Unmatched',
};

export function conversationStatusClass(status: types.ConversationStatus): string {
  switch (status) {
    case 'OPEN':
      return 'bg-blue-100 text-blue-800';
    case 'WAITING_ON_CUSTOMER':
      return 'bg-amber-100 text-amber-800';
    case 'CLOSED':
      return 'bg-gray-200 text-gray-700';
    default:
      return 'bg-purple-100 text-purple-800';
  }
}

/** Whether the role may use the staff inbox under the company setting (mirrors the backend gate). */
export function inboxAllowed(role: string | undefined, access: types.InboxAccess | undefined): boolean {
  if (role === types.UserRole.ADMIN || role === types.UserRole.OFFICE_MANAGER) return true;
  return role === types.UserRole.TECHNICIAN && access === 'EVERYONE';
}

/** Clear text for the send errors staff can act on; anything else uses the server's message. */
export function conversationErrorMessage(error: unknown, fallback: string): string {
  switch (getErrorCode(error)) {
    case 'CUSTOMER_OPTED_OUT':
      return 'This customer opted out of emails. You can answer them only after they write to you first (by email or the customer portal).';
    case 'CUSTOMER_NO_EMAIL':
      return 'This customer has no email address. Add one on the customer before starting a conversation.';
    case 'CUSTOMER_EMAIL_INVALID':
      return "This customer's email address bounced or reported spam. Update the address on the customer first.";
    case 'CONVERSATION_UNMATCHED':
      return 'This email is not linked to a customer yet. Attach it to a customer first.';
    case 'CONVERSATION_NOT_UNMATCHED':
      return 'This email was already attached or discarded. Reload the list.';
    case 'JOB_CUSTOMER_MISMATCH':
      return 'That job belongs to another customer.';
    case 'INVALID_ASSIGNEE':
      return 'Assign the conversation to an active user who can see the inbox.';
    case 'PORTAL_THREAD_CUSTOMER':
      return 'A customer portal thread stays with the customer of that portal login.';
    case 'INBOX_ACCESS_DENIED':
      return 'The inbox is not available to your role.';
    default:
      return getErrorMessage(error, fallback);
  }
}

/** One line about an outbound message's email: what the outbox did with it. */
export function deliveryText(delivery: types.MessageDelivery | null): { label: string; tone: 'ok' | 'pending' | 'bad' } | null {
  if (!delivery) return null;
  if (delivery.deliveryStatus === 'DELIVERED') return { label: 'Delivered', tone: 'ok' };
  if (delivery.deliveryStatus === 'BOUNCED') return { label: 'Bounced', tone: 'bad' };
  if (delivery.deliveryStatus === 'COMPLAINED') return { label: 'Marked as spam', tone: 'bad' };
  switch (delivery.status) {
    case 'SENT':
      return { label: 'Sent', tone: 'ok' };
    case 'PENDING':
    case 'SENDING':
      return { label: 'Queued', tone: 'pending' };
    case 'FAILED':
      return { label: 'Failed to send', tone: 'bad' };
    case 'SKIPPED':
      return { label: `Not sent: ${delivery.skipReasonText ?? delivery.skipReason ?? 'skipped'}`, tone: 'bad' };
    default:
      return { label: delivery.status, tone: 'pending' };
  }
}

export function formatWhen(iso: string | null, now = new Date()): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const sameDay = date.toDateString() === now.toDateString();
  return sameDay
    ? date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: date.getFullYear() === now.getFullYear() ? undefined : 'numeric' });
}

/** A random key so a double-clicked send is sent once (the server dedupes by it). */
export function sendKey(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Who wrote a message, for the thread. */
export function messageAuthor(message: types.ConversationMessage, customerName: string | null): string {
  if (message.direction === 'NOTE') return `${message.authorName ?? 'Staff'} (internal note)`;
  if (message.direction === 'OUTBOUND') return message.authorName ?? 'Staff';
  const via = message.channel === 'PORTAL' ? ' via the portal' : '';
  return `${customerName ?? message.fromAddress ?? 'Customer'}${via}`;
}
